import React, { Fragment, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import date from 'date-and-time';

import HeaderEjercicios from '../../../AuxiliaresPaginas/HeaderEjercicios';
import Instrucciones from '../../../Escritorio/Instrucciones';
import useConfigAuth from '../../../../Hooks/useConfigAuth';
import BotoneraBottom from '../../Compartidos/BotoneraBottom';

import {
    finalizarEjercicioAction,
    setearEjercicioAction,
    setearUrlAnteriorAction
} from '../../../../actions/estadoEjercicioActions';

import { almacenarAction } from '../../../../actions/almacenarEjercicioActions';

import {
    AvanzarImg,
    AvanzarBtn,
    PantallaM150,
    ContenidoM150,
    RectaM150,
    BottomM150
} from '../../../Css/StyledMatematica';


const miniInstrucciones =
    'Representa la cantidad de chocolate y ubícala en la recta numérica.';


// =============================================================================
// CONFIGURACIÓN
// =============================================================================

const MAX_PARTES = 6;
const CANTIDAD_EJERCICIOS = 5;


// =============================================================================
// RECTA NUMÉRICA
// =============================================================================

const X0 = 80;
const UNIT = 88;
const Y = 190;

const W_SVG = X0 * 2 + UNIT * 10;
const H_SVG = 315;

const TRAY_Y = Y - 64;
const TRAY_H = 46;
const CTRL_Y = TRAY_Y - 22;


// =============================================================================
// COLORES
// =============================================================================

const CHOCO = '#7B4A2A';
const CHOCO_LIGHT = '#94592F';
const CHOCO_SHINE = '#B07446';
const CHOCO_SHADOW = '#5A3420';

const ACCENT = '#23AAAF';
const MARK = '#E8662B';
const OK = '#2E8B57';
const INK = '#222';


const btn = {
    fontFamily: 'Nunito',
    fontSize: 18,
    fontWeight: 600,
    padding: '9px 18px',
    borderRadius: 10,
    border: `2px solid ${ACCENT}`,
    background: '#FFFFFF',
    color: ACCENT,
    cursor: 'pointer'
};


// =============================================================================
// AUXILIARES
// =============================================================================

const toX = (valor) =>
    X0 + valor * UNIT;


const toV = (x) =>
    (x - X0) / UNIT;


// -----------------------------------------------------------------------------
// MEZCLAR
// -----------------------------------------------------------------------------

const mezclar = (array) => {

    const nuevaLista = [
        ...array
    ];


    for (
        let i =
            nuevaLista.length - 1;

        i > 0;

        i--
    ) {

        const j =
            Math.floor(
                Math.random() *
                (
                    i + 1
                )
            );


        [
            nuevaLista[i],
            nuevaLista[j]
        ] = [
            nuevaLista[j],
            nuevaLista[i]
        ];
    }


    return nuevaLista;
};


// -----------------------------------------------------------------------------
// ENTERO ALEATORIO
// -----------------------------------------------------------------------------

const enteroAleatorio = (
    min,
    max
) => {

    return (
        Math.floor(
            Math.random() *
            (
                max -
                min +
                1
            )
        ) +
        min
    );
};


// -----------------------------------------------------------------------------
// MODALIDADES
//
// Son 5 ejercicios.
//
// Cada sesión tendrá:
// - 2 enteros + 3 fracciones
// o
// - 3 enteros + 2 fracciones
//
// Así se mantiene aproximadamente 50 / 50.
//
// Además, nunca aparecen 3 ejercicios seguidos
// de la misma modalidad.
// -----------------------------------------------------------------------------

const generarModalidades = () => {

    const cantidadEnteros =
        Math.random() < 0.5

            ? 2

            : 3;


    const cantidadFracciones =
        CANTIDAD_EJERCICIOS -
        cantidadEnteros;


    const base = [

        ...Array(
            cantidadEnteros
        ).fill(
            'entero'
        ),

        ...Array(
            cantidadFracciones
        ).fill(
            'fraccion'
        )

    ];


    let modalidades = [];


    do {

        modalidades =
            mezclar(
                base
            );

    } while (

        modalidades.some(
            (
                tipo,
                index
            ) =>

                index >= 2 &&

                tipo ===
                    modalidades[
                        index - 1
                    ] &&

                tipo ===
                    modalidades[
                        index - 2
                    ]
        )

    );


    return modalidades;
};


// -----------------------------------------------------------------------------
// GENERAR EJERCICIOS
//
// ENTEROS:
// 1 a 10.
//
// FRACCIONES:
// denominador 2 a 6.
// partes comidas 1 hasta denominador.
// numerador = partes que quedan.
//
// Los enteros se guardan internamente como n/1.
// Ejemplo:
// 6 chocolates = 6/1.
// -----------------------------------------------------------------------------

const generarEjercicios = () => {

    const modalidades =
        generarModalidades();


    return modalidades.map(
        (
            tipo,
            index
        ) => {


            // =============================================================
            // ENTERO
            // =============================================================

            if (
                tipo ===
                'entero'
            ) {

                const valor =
                    enteroAleatorio(
                        1,
                        10
                    );


                return {

                    id:
                        `${index + 1}`,

                    tipo:
                        'entero',

                    numerador:
                        valor,

                    denominador:
                        1,

                    comido:
                        0

                };
            }


            // =============================================================
            // FRACCIÓN
            // =============================================================

            const denominador =
                enteroAleatorio(
                    2,
                    MAX_PARTES
                );


            const comido =
                enteroAleatorio(
                    1,
                    denominador
                );


            const numerador =
                denominador -
                comido;


            return {

                id:
                    `${index + 1}`,

                tipo:
                    'fraccion',

                numerador,

                denominador,

                comido

            };

        }
    );
};


// =============================================================================
// CHOCOLATE
// =============================================================================

const ChocolateBar = () => {

    const cols = 6;
    const rows = 3;

    const cell = 34;
    const border = 6;


    const width =
        cols *
        cell +
        border *
        2;


    const height =
        rows *
        cell +
        border *
        2;


    const cells = [];


    for (
        let c = 0;
        c < cols;
        c++
    ) {

        for (
            let r = 0;
            r < rows;
            r++
        ) {

            const x =
                border +
                c *
                cell +
                3;


            const y =
                border +
                r *
                cell +
                3;


            const size =
                cell -
                6;


            cells.push(

                <g
                    key={
                        `${c}-${r}`
                    }
                >

                    <rect
                        x={
                            x
                        }
                        y={
                            y
                        }
                        width={
                            size
                        }
                        height={
                            size
                        }
                        rx={
                            3
                        }
                        fill={
                            CHOCO_LIGHT
                        }
                    />


                    <path
                        d={`
                            M${x + 2} ${y + size - 2}
                            L${x + 2} ${y + 2}
                            L${x + size - 2} ${y + 2}
                        `}
                        fill="none"
                        stroke={
                            CHOCO_SHINE
                        }
                        strokeWidth={
                            2.5
                        }
                        strokeLinecap="round"
                    />


                    <path
                        d={`
                            M${x + size - 2} ${y + 2}
                            L${x + size - 2} ${y + size - 2}
                            L${x + 2} ${y + size - 2}
                        `}
                        fill="none"
                        stroke={
                            CHOCO_SHADOW
                        }
                        strokeWidth={
                            2.5
                        }
                        strokeLinecap="round"
                    />

                </g>
            );
        }
    }


    return (

        <svg
            width={
                width
            }
            height={
                height
            }
            viewBox={
                `0 0 ${width} ${height}`
            }
            aria-hidden="true"
        >

            <rect
                x={
                    0
                }
                y={
                    0
                }
                width={
                    width
                }
                height={
                    height
                }
                rx={
                    8
                }
                fill={
                    CHOCO
                }
            />


            {
                cells
            }

        </svg>
    );
};


// =============================================================================
// BOTONES + / -
// =============================================================================

const MiniButton = ({
    cx,
    cy,
    label,
    onClick,
    disabled,
    ariaLabel
}) => {

    return (

        <g
            onClick={
                disabled

                    ? undefined

                    : onClick
            }
            style={{
                cursor:
                    disabled

                        ? 'default'

                        : 'pointer'
            }}
            role="button"
            aria-label={
                ariaLabel
            }
            opacity={
                disabled

                    ? 0.35

                    : 1
            }
        >

            <circle
                cx={
                    cx
                }
                cy={
                    cy
                }
                r={
                    11
                }
                fill="#FFFFFF"
                stroke={
                    ACCENT
                }
                strokeWidth={
                    2
                }
            />


            <text
                x={
                    cx
                }
                y={
                    cy +
                    6
                }
                textAnchor="middle"
                fontSize="18"
                fontWeight="700"
                fill={
                    ACCENT
                }
            >
                {
                    label
                }
            </text>

        </g>
    );
};


// =============================================================================
// ETIQUETA DE FRACCIÓN
//
// No simplifica.
//
// Ejemplo:
// 2/6 queda 2/6.
// =============================================================================

const EtiquetaFraccion = ({
    x,
    y,
    numerador,
    denominador,
    color
}) => {

    return (

        <g
            fill={
                color
            }
            fontWeight="800"
            textAnchor="middle"
        >

            <text
                x={
                    x
                }
                y={
                    y +
                    8
                }
                fontSize="18"
            >
                {
                    numerador
                }
            </text>


            <line
                x1={
                    x -
                    10
                }
                y1={
                    y +
                    14
                }
                x2={
                    x +
                    10
                }
                y2={
                    y +
                    14
                }
                stroke={
                    color
                }
                strokeWidth={
                    2.5
                }
            />


            <text
                x={
                    x
                }
                y={
                    y +
                    34
                }
                fontSize="18"
            >
                {
                    denominador
                }
            </text>

        </g>
    );
};


// =============================================================================
// CONSIGNA
// =============================================================================

const Consigna = ({
    ejercicio
}) => {

    if (
        !ejercicio
    ) {

        return null;
    }


    const {
        tipo,
        numerador,
        denominador,
        comido
    } = ejercicio;


    // -------------------------------------------------------------------------
    // ENTERO
    // -------------------------------------------------------------------------

    if (
        tipo ===
        'entero'
    ) {

        const textoChocolate =
            numerador === 1

                ? 'Me regalaron 1 chocolate.'

                : `Me regalaron ${numerador} chocolates.`;


        return (

            <div
                style={{
                    fontFamily:
                        'Nunito',

                    color:
                        INK,

                    maxWidth:
                        500
                }}
            >

                <p
                    style={{
                        fontSize:
                            24,

                        fontWeight:
                            500,

                        margin:
                            '4px 0',

                        lineHeight:
                            1.35
                    }}
                >
                    {
                        textoChocolate
                    }
                </p>


                <p
                    style={{
                        fontSize:
                            24,

                        fontWeight:
                            600,

                        margin:
                            '8px 0 4px 0',

                        lineHeight:
                            1.35
                    }}
                >
                    ¿Cuántos chocolates tengo?
                </p>


                <p
                    style={{
                        fontSize:
                            24,

                        fontWeight:
                            500,

                        margin:
                            '4px 0',

                        lineHeight:
                            1.35
                    }}
                >
                    Ubicalos en la recta numérica.
                </p>

            </div>
        );
    }


    // -------------------------------------------------------------------------
    // FRACCIÓN
    // -------------------------------------------------------------------------

    const textoComido =
        comido === 1

            ? 'Me comí 1 parte.'

            : `Me comí ${comido} partes.`;


    return (

        <div
            style={{
                fontFamily:
                    'Nunito',

                color:
                    INK,

                maxWidth:
                    500
            }}
        >

            <p
                style={{
                    fontSize:
                        24,

                    fontWeight:
                        500,

                    margin:
                        '4px 0',

                    lineHeight:
                        1.35
                }}
            >
                Tengo un chocolate.
            </p>


            <p
                style={{
                    fontSize:
                        24,

                    fontWeight:
                        500,

                    margin:
                        '4px 0',

                    lineHeight:
                        1.35
                }}
            >
                Lo partí en {denominador} partes iguales.
            </p>


            <p
                style={{
                    fontSize:
                        24,

                    fontWeight:
                        500,

                    margin:
                        '4px 0',

                    lineHeight:
                        1.35
                }}
            >
                {
                    textoComido
                }
            </p>


            <p
                style={{
                    fontSize:
                        24,

                    fontWeight:
                        600,

                    margin:
                        '8px 0 4px 0',

                    lineHeight:
                        1.35
                }}
            >
                ¿Cuánto chocolate me queda?
            </p>


            <p
                style={{
                    fontSize:
                        24,

                    fontWeight:
                        500,

                    margin:
                        '4px 0',

                    lineHeight:
                        1.35
                }}
            >
                Ubicalo en la recta numérica.
            </p>

        </div>
    );
};


// =============================================================================
// M150
// =============================================================================

const M150 = () => {

    const dispatch =
        useDispatch();


    const navigate =
        useNavigate();


    const svgRef =
        useRef(null);


    const [config] =
        useConfigAuth();


    // -------------------------------------------------------------------------
    // EJERCICIOS
    // -------------------------------------------------------------------------

    const [
        ejercicios,
        setEjercicios
    ] = useState([]);


    const [
        count,
        setCount
    ] = useState(1);


    const [
        indexes,
        setIndexes
    ] = useState();


    const [
        disable,
        setDisable
    ] = useState(false);


    // -------------------------------------------------------------------------
    // BANDEJA
    // -------------------------------------------------------------------------

    const [
        bandejaVisible,
        setBandejaVisible
    ] = useState(false);


    const [
        partes,
        setPartes
    ] = useState(1);


    const [
        pintadas,
        setPintadas
    ] = useState([]);


    // -------------------------------------------------------------------------
    // RECTA
    // -------------------------------------------------------------------------

    const [
        mark,
        setMark
    ] = useState(null);


    const [
        resuelto,
        setResuelto
    ] = useState(false);


    // -------------------------------------------------------------------------
    // FEEDBACK
    // -------------------------------------------------------------------------

    const [
        feedback,
        setFeedback
    ] = useState(null);


    // -------------------------------------------------------------------------
    // ESTADÍSTICAS
    // -------------------------------------------------------------------------

    const [
        contarErrores,
        setContarErrores
    ] = useState(0);


    const [
        erroresOrden,
        setErroresOrden
    ] = useState([]);


    const [
        contarTiempo,
        setContarTiempo
    ] = useState([]);


    const [
        ultimoTiempo,
        setUltimoTiempo
    ] = useState(false);


    // -------------------------------------------------------------------------
    // REDUX
    // -------------------------------------------------------------------------

    const iniciar =
        useSelector(
            state =>
                state.ejercicio.iniciar
        );


    const finalizar =
        useSelector(
            state =>
                state.ejercicio.finalizar
        );


    const id_paciente =
        useSelector(
            state =>
                state.usuario.id_paciente
        );


    const sesion =
        useSelector(
            state =>
                state.ejercicio.sesion
        );


    // -------------------------------------------------------------------------
    // EJERCICIO ACTUAL
    // -------------------------------------------------------------------------

    const ejercicioActual =
        ejercicios.length > 0

            ? ejercicios[
                count - 1
            ]

            : null;


    // -------------------------------------------------------------------------
    // SONIDOS
    // -------------------------------------------------------------------------

    const GoodWork = () => {

        new Audio(
            '/sounds/sonido_bien.mp3'
        ).play();
    };


    const TryAgain = () => {

        new Audio(
            '/sounds/sonidoMal.mp3'
        ).play();
    };


    // -------------------------------------------------------------------------
    // PONER BANDEJA
    // -------------------------------------------------------------------------

    const ponerBandeja = (
        e
    ) => {

        e.preventDefault();


        if (
            disable ||
            resuelto
        ) {

            return;
        }


        setBandejaVisible(
            true
        );


        setPartes(
            1
        );


        setPintadas(
            []
        );


        setMark(
            null
        );


        setFeedback(
            null
        );
    };


    // -------------------------------------------------------------------------
    // SACAR BANDEJA
    // -------------------------------------------------------------------------

    const sacarBandeja = (
        e
    ) => {

        e.preventDefault();


        if (
            disable ||
            resuelto
        ) {

            return;
        }


        setBandejaVisible(
            false
        );


        setPartes(
            1
        );


        setPintadas(
            []
        );


        setMark(
            null
        );


        setFeedback(
            null
        );
    };


    // -------------------------------------------------------------------------
    // CAMBIAR PARTES
    // -------------------------------------------------------------------------

    const cambiarPartes = (
        delta
    ) => {

        if (
            disable ||
            resuelto
        ) {

            return;
        }


        const nuevasPartes =
            Math.max(
                1,

                Math.min(
                    MAX_PARTES,

                    partes +
                    delta
                )
            );


        if (
            nuevasPartes ===
            partes
        ) {

            return;
        }


        setPartes(
            nuevasPartes
        );


        /*
        Si cambia la división,
        vaciamos la representación.
        */

        setPintadas(
            []
        );


        setMark(
            null
        );


        setFeedback(
            null
        );
    };


    // -------------------------------------------------------------------------
    // PINTAR / DESPINTAR
    // -------------------------------------------------------------------------

    const togglePaint = (
        index
    ) => {

        if (
            disable ||
            resuelto
        ) {

            return;
        }


        if (
            pintadas.includes(
                index
            )
        ) {

            setPintadas(
                pintadas.filter(
                    item =>
                        item !==
                        index
                )
            );

        } else {

            setPintadas([
                ...pintadas,
                index
            ]);
        }


        setFeedback(
            null
        );
    };


    // -------------------------------------------------------------------------
    // CLICK EN RECTA
    // -------------------------------------------------------------------------

    const handleLineClick = (
        evt
    ) => {

        if (
            disable ||
            resuelto
        ) {

            return;
        }


        const svg =
            svgRef.current;


        if (
            !svg
        ) {

            return;
        }


        const point =
            svg.createSVGPoint();


        point.x =
            evt.clientX;


        point.y =
            evt.clientY;


        const matrix =
            svg.getScreenCTM();


        if (
            !matrix
        ) {

            return;
        }


        const position =
            point.matrixTransform(
                matrix.inverse()
            );


        let valor =
            Math.max(
                0,

                Math.min(
                    10,

                    toV(
                        position.x
                    )
                )
            );


        // ---------------------------------------------------------------------
        // PUNTOS DE REFERENCIA
        // ---------------------------------------------------------------------

        const candidatos =
            Array.from(
                {
                    length:
                        11
                },

                (
                    _,
                    i
                ) =>
                    i
            );


        /*
        Si hay bandeja dividida,
        agregamos sus fracciones como
        puntos posibles de snap.
        */

        if (
            bandejaVisible
        ) {

            for (
                let i = 1;

                i < partes;

                i++
            ) {

                candidatos.push(
                    i /
                    partes
                );
            }
        }


        const masCercano =
            candidatos.reduce(
                (
                    anterior,
                    actual
                ) =>

                    Math.abs(
                        actual -
                        valor
                    )

                    <

                    Math.abs(
                        anterior -
                        valor
                    )

                        ? actual

                        : anterior,

                candidatos[0]
            );


        if (
            Math.abs(
                masCercano -
                valor
            ) *
            UNIT <
            10
        ) {

            valor =
                masCercano;
        }


        setMark(
            Math.round(
                valor *
                1000
            ) /
            1000
        );


        setFeedback(
            null
        );
    };


    // -------------------------------------------------------------------------
    // ERROR
    // -------------------------------------------------------------------------

    const marcarError = (
        texto
    ) => {

        setContarErrores(
            errores =>
                errores + 1
        );


        setFeedback({
            ok:
                false,

            text:
                texto
        });


        setDisable(
            true
        );


        TryAgain();


        setTimeout(
            () => {

                setDisable(
                    false
                );

            },

            500
        );
    };


    // -------------------------------------------------------------------------
    // LIMPIAR
    // -------------------------------------------------------------------------

    const limpiarEjercicio = () => {

        setBandejaVisible(
            false
        );


        setPartes(
            1
        );


        setPintadas(
            []
        );


        setMark(
            null
        );


        setFeedback(
            null
        );


        setResuelto(
            false
        );


        setDisable(
            false
        );
    };


    // -------------------------------------------------------------------------
    // VERIFICAR
    // -------------------------------------------------------------------------

    const verificar = (
        e
    ) => {

        e.preventDefault();


        if (
            disable ||
            resuelto ||
            !ejercicioActual
        ) {

            return;
        }


        const {
            tipo,
            numerador,
            denominador
        } = ejercicioActual;


        // =====================================================================
        // FRACCIÓN
        // =====================================================================

        if (
            tipo ===
            'fraccion'
        ) {


            // -----------------------------------------------------------------
            // 1. BANDEJA
            // -----------------------------------------------------------------

            if (
                !bandejaVisible
            ) {

                marcarError(
                    'Poné una bandeja. La bandeja representa el chocolate entero.'
                );


                return;
            }


            // -----------------------------------------------------------------
            // 2. DENOMINADOR
            // -----------------------------------------------------------------

            if (
                partes !==
                denominador
            ) {

                marcarError(
                    `¿En cuántas partes partí el chocolate? Dividí la bandeja en ${denominador} partes iguales.`
                );


                return;
            }


            // -----------------------------------------------------------------
            // 3. NUMERADOR
            // -----------------------------------------------------------------

            if (
                pintadas.length !==
                numerador
            ) {

                if (
                    pintadas.length >
                    numerador
                ) {

                    marcarError(
                        'Pintaste de más. Fijate cuánto chocolate me queda.'
                    );

                } else {

                    marcarError(
                        'Todavía falta pintar. Fijate cuánto chocolate me queda.'
                    );
                }


                return;
            }
        }


        // =====================================================================
        // RECTA NUMÉRICA
        // =====================================================================

        if (
            mark ===
            null
        ) {

            marcarError(

                tipo ===
                    'entero'

                    ?
                    'Ahora hacé clic en la recta numérica donde va esa cantidad de chocolates.'

                    :
                    'Ahora hacé clic en la recta numérica donde va esa cantidad.'
            );


            return;
        }


        const respuestaCorrecta =
            numerador /
            denominador;


        const distancia =
            Math.abs(
                mark -
                respuestaCorrecta
            ) *
            UNIT;


        if (
            distancia >
            6
        ) {

            marcarError(

                tipo ===
                    'entero'

                    ?
                    'Fijate cuántos chocolates tenés y buscá ese número en la recta.'

                    :
                    'Mirá la bandeja. Empezando desde el 0, ¿dónde termina esa cantidad?'
            );


            return;
        }


        // =====================================================================
        // CORRECTO
        // =====================================================================

        setDisable(
            true
        );


        setResuelto(
            true
        );


        setFeedback({
            ok:
                true,

            text:
                '¡Muy bien!'
        });


        GoodWork();


        // ---------------------------------------------------------------------
        // ERRORES DE ESTE ÍTEM
        // ---------------------------------------------------------------------

        const erroresAnteriores =
            erroresOrden.reduce(
                (
                    total,
                    valor
                ) =>
                    total +
                    valor,

                0
            );


        const erroresEsteEjercicio =
            contarErrores -
            erroresAnteriores;


        setErroresOrden([
            ...erroresOrden,
            erroresEsteEjercicio
        ]);


        // ---------------------------------------------------------------------
        // SIGUIENTE
        // ---------------------------------------------------------------------

        if (
            count <
            indexes
        ) {

            setTimeout(
                () => {

                    limpiarEjercicio();


                    setCount(
                        contador =>
                            contador +
                            1
                    );

                },

                1000
            );

        } else {

            setTimeout(
                () => {

                    setUltimoTiempo(
                        true
                    );


                    dispatch(
                        finalizarEjercicioAction()
                    );

                },

                1500
            );
        }
    };


    // =========================================================================
    // GENERAR EJERCICIOS
    // =========================================================================

    useEffect(
        () => {

            const modelo =
                generarEjercicios();


            setEjercicios(
                modelo
            );


            setIndexes(
                modelo.length
            );


            dispatch(
                setearEjercicioAction(
                    'M150'
                )
            );


            dispatch(
                setearUrlAnteriorAction(
                    'matematica/fracciones/'
                )
            );


            // eslint-disable-next-line react-hooks/exhaustive-deps
        },

        []
    );


    // =========================================================================
    // TIEMPOS
    // =========================================================================

    useEffect(
        () => {

            if (
                iniciar ===
                false &&
                count ===
                indexes
            ) {

                return;
            }


            if (
                iniciar ===
                false &&
                count ===
                1
            ) {

                return;
            }


            const newList =
                JSON.parse(
                    JSON.stringify(
                        contarTiempo
                    )
                );


            newList.push(
                new Date()
            );


            setContarTiempo(
                newList
            );


            // eslint-disable-next-line react-hooks/exhaustive-deps
        },

        [
            iniciar,
            count,
            ultimoTiempo
        ]
    );


    // =========================================================================
    // FINALIZAR Y GUARDAR
    // =========================================================================

    useEffect(
        () => {

            if (
                !finalizar
            ) {

                return;
            }


            const tiempo =
                JSON.parse(
                    JSON.stringify(
                        contarTiempo
                    )
                );


            const conseguirTiempos = (
                array
            ) => {

                const tiempos = [];


                for (
                    let i = 0;

                    i <
                    array.length -
                    1;

                    i++
                ) {

                    const despues =
                        new Date(
                            array[
                                i + 1
                            ]
                        );


                    const antes =
                        new Date(
                            array[
                                i
                            ]
                        );


                    const segundos =
                        Math.round(
                            (
                                date
                                    .subtract(
                                        despues,
                                        antes
                                    )
                                    .toSeconds()
                            ) *
                            10
                        ) /
                        10;


                    tiempos.push(
                        segundos
                    );
                }


                return tiempos;
            };


            /*
            Se mantiene el cálculo de tiempos
            del M150 actual.
            */

            conseguirTiempos(
                tiempo
            );


            /*
            Lista usada.

            Fracción:
            2/6

            Entero:
            6/1
            */

            const listaFracciones =
                ejercicios.map(
                    item =>
                        `${item.numerador}/${item.denominador}`
                );


            const informacion = {

                config,

                payload: {

                    ejercicio:
                        'M150',

                    errores:
                        contarErrores,

                    lista_usada:
                        listaFracciones,

                    erroresOrden,

                    id_paciente,

                    sesion
                }
            };


            dispatch(
                almacenarAction(
                    informacion
                )
            );


            navigate(
                '/escritorio/metodos/finalizar-ejercicio'
            );


            // eslint-disable-next-line react-hooks/exhaustive-deps
        },

        [
            finalizar
        ]
    );


    // =========================================================================
    // RENDER
    // =========================================================================

    return (

        <Fragment>


            <HeaderEjercicios
                estrellas={
                    true
                }
                carrito={
                    true
                }
                minMay={
                    false
                }
            />


            {
                iniciar

                    ?

                    ejercicioActual

                        ?

                        <PantallaM150>


                            <ContenidoM150>


                                {/* ================================================= */}
                                {/* CONSIGNA + CHOCOLATE                              */}
                                {/* ================================================= */}

                                <div
                                    style={{
                                        width:
                                            '100%',

                                        display:
                                            'flex',

                                        flexWrap:
                                            'wrap',

                                        alignItems:
                                            'center',

                                        justifyContent:
                                            'center',

                                        gap:
                                            35
                                    }}
                                >

                                    <Consigna
                                        ejercicio={
                                            ejercicioActual
                                        }
                                    />


                                    <ChocolateBar />

                                </div>


                                {/* ================================================= */}
                                {/* RECTA NUMÉRICA                                    */}
                                {/* ================================================= */}

                                <RectaM150
                                    ref={
                                        svgRef
                                    }
                                    viewBox={
                                        `0 0 ${W_SVG} ${H_SVG}`
                                    }
                                    role="img"
                                    aria-label="Recta numérica del 0 al 10"
                                >


                                    {/* FONDO */}

                                    <rect
                                        x={
                                            0
                                        }
                                        y={
                                            0
                                        }
                                        width={
                                            W_SVG
                                        }
                                        height={
                                            H_SVG
                                        }
                                        fill="#FFFFFF"
                                    />


                                    {/* LÍNEA PRINCIPAL */}

                                    <line
                                        x1={
                                            toX(
                                                0
                                            ) -
                                            50
                                        }
                                        y1={
                                            Y
                                        }
                                        x2={
                                            toX(
                                                10
                                            ) +
                                            50
                                        }
                                        y2={
                                            Y
                                        }
                                        stroke={
                                            INK
                                        }
                                        strokeWidth={
                                            3
                                        }
                                    />


                                    {/* FLECHA IZQUIERDA */}

                                    <polygon
                                        points={`
                                            ${toX(0) - 64},${Y}
                                            ${toX(0) - 48},${Y - 9}
                                            ${toX(0) - 48},${Y + 9}
                                        `}
                                        fill={
                                            INK
                                        }
                                    />


                                    {/* FLECHA DERECHA */}

                                    <polygon
                                        points={`
                                            ${toX(10) + 64},${Y}
                                            ${toX(10) + 48},${Y - 9}
                                            ${toX(10) + 48},${Y + 9}
                                        `}
                                        fill={
                                            INK
                                        }
                                    />


                                    {/* ================================================= */}
                                    {/* NÚMEROS 0 A 10                                    */}
                                    {/* ================================================= */}

                                    {
                                        Array
                                            .from({
                                                length:
                                                    11
                                            })
                                            .map(
                                                (
                                                    _,
                                                    numero
                                                ) => (

                                                    <g
                                                        key={
                                                            numero
                                                        }
                                                    >

                                                        <line
                                                            x1={
                                                                toX(
                                                                    numero
                                                                )
                                                            }
                                                            y1={
                                                                Y -
                                                                12
                                                            }
                                                            x2={
                                                                toX(
                                                                    numero
                                                                )
                                                            }
                                                            y2={
                                                                Y +
                                                                12
                                                            }
                                                            stroke={
                                                                INK
                                                            }
                                                            strokeWidth={
                                                                3
                                                            }
                                                        />


                                                        <text
                                                            x={
                                                                toX(
                                                                    numero
                                                                )
                                                            }
                                                            y={
                                                                Y +
                                                                40
                                                            }
                                                            textAnchor="middle"
                                                            fontSize="22"
                                                            fontWeight="600"
                                                            fill={
                                                                INK
                                                            }
                                                        >
                                                            {
                                                                numero
                                                            }
                                                        </text>

                                                    </g>
                                                )
                                            )
                                    }


                                    {/* ================================================= */}
                                    {/* ZONA CLICKABLE                                    */}
                                    {/* ================================================= */}

                                    <rect
                                        x={
                                            toX(
                                                0
                                            ) -
                                            20
                                        }
                                        y={
                                            Y -
                                            14
                                        }
                                        width={
                                            UNIT *
                                            10 +
                                            40
                                        }
                                        height={
                                            62
                                        }
                                        fill="transparent"
                                        style={{
                                            cursor:

                                                disable ||
                                                resuelto

                                                    ?
                                                    'default'

                                                    :
                                                    'pointer'
                                        }}
                                        onClick={
                                            handleLineClick
                                        }
                                    />


                                    {/* ================================================= */}
                                    {/* BANDEJA                                           */}
                                    {/* ================================================= */}

                                    {
                                        bandejaVisible && (

                                            <g>


                                                {/* MENOS */}

                                                <MiniButton
                                                    cx={
                                                        toX(
                                                            0
                                                        ) +
                                                        14
                                                    }
                                                    cy={
                                                        CTRL_Y
                                                    }
                                                    label="−"
                                                    ariaLabel="Menos partes"
                                                    disabled={
                                                        disable ||
                                                        resuelto ||
                                                        partes <=
                                                        1
                                                    }
                                                    onClick={
                                                        () =>
                                                            cambiarPartes(
                                                                -1
                                                            )
                                                    }
                                                />


                                                {/* CANTIDAD DE PARTES */}

                                                <text
                                                    x={
                                                        toX(
                                                            0
                                                        ) +
                                                        UNIT /
                                                        2
                                                    }
                                                    y={
                                                        CTRL_Y +
                                                        7
                                                    }
                                                    textAnchor="middle"
                                                    fontSize="20"
                                                    fontWeight="700"
                                                    fill={
                                                        INK
                                                    }
                                                >
                                                    {
                                                        partes
                                                    }
                                                </text>


                                                {/* MÁS */}

                                                <MiniButton
                                                    cx={
                                                        toX(
                                                            1
                                                        ) -
                                                        14
                                                    }
                                                    cy={
                                                        CTRL_Y
                                                    }
                                                    label="+"
                                                    ariaLabel="Más partes"
                                                    disabled={
                                                        disable ||
                                                        resuelto ||
                                                        partes >=
                                                        MAX_PARTES
                                                    }
                                                    onClick={
                                                        () =>
                                                            cambiarPartes(
                                                                1
                                                            )
                                                    }
                                                />


                                                {/* PARTES */}

                                                {
                                                    Array
                                                        .from({
                                                            length:
                                                                partes
                                                        })
                                                        .map(
                                                            (
                                                                _,
                                                                index
                                                            ) => {

                                                                const anchoParte =
                                                                    UNIT /
                                                                    partes;


                                                                return (

                                                                    <rect
                                                                        key={
                                                                            index
                                                                        }
                                                                        x={
                                                                            toX(
                                                                                0
                                                                            ) +
                                                                            index *
                                                                            anchoParte
                                                                        }
                                                                        y={
                                                                            TRAY_Y
                                                                        }
                                                                        width={
                                                                            anchoParte
                                                                        }
                                                                        height={
                                                                            TRAY_H
                                                                        }
                                                                        fill={
                                                                            pintadas.includes(
                                                                                index
                                                                            )

                                                                                ?
                                                                                CHOCO

                                                                                :
                                                                                '#F4F1EC'
                                                                        }
                                                                        stroke="#333"
                                                                        strokeWidth={
                                                                            2
                                                                        }
                                                                        style={{
                                                                            cursor:

                                                                                disable ||
                                                                                resuelto

                                                                                    ?
                                                                                    'default'

                                                                                    :
                                                                                    'pointer'
                                                                        }}
                                                                        onClick={
                                                                            () =>
                                                                                togglePaint(
                                                                                    index
                                                                                )
                                                                        }
                                                                    />

                                                                );
                                                            }
                                                        )
                                                }


                                                {/* BORDE */}

                                                <rect
                                                    x={
                                                        toX(
                                                            0
                                                        )
                                                    }
                                                    y={
                                                        TRAY_Y
                                                    }
                                                    width={
                                                        UNIT
                                                    }
                                                    height={
                                                        TRAY_H
                                                    }
                                                    fill="none"
                                                    stroke="#333"
                                                    strokeWidth={
                                                        3
                                                    }
                                                    rx={
                                                        3
                                                    }
                                                    pointerEvents="none"
                                                />

                                            </g>
                                        )
                                    }


                                    {/* ================================================= */}
                                    {/* MARCA DEL ALUMNO                                  */}
                                    {/* ================================================= */}

                                    {
                                        mark !==
                                        null && (

                                            <g
                                                pointerEvents="none"
                                            >

                                                <circle
                                                    cx={
                                                        toX(
                                                            mark
                                                        )
                                                    }
                                                    cy={
                                                        Y
                                                    }
                                                    r={
                                                        9
                                                    }
                                                    fill={
                                                        resuelto

                                                            ?
                                                            OK

                                                            :
                                                            MARK
                                                    }
                                                    stroke="#FFFFFF"
                                                    strokeWidth={
                                                        2
                                                    }
                                                />


                                                {
                                                    resuelto && (

                                                        <g>

                                                            <line
                                                                x1={
                                                                    toX(
                                                                        mark
                                                                    )
                                                                }
                                                                y1={
                                                                    Y +
                                                                    10
                                                                }
                                                                x2={
                                                                    toX(
                                                                        mark
                                                                    )
                                                                }
                                                                y2={
                                                                    Y +
                                                                    50
                                                                }
                                                                stroke={
                                                                    OK
                                                                }
                                                                strokeWidth={
                                                                    2
                                                                }
                                                                strokeDasharray="4 4"
                                                            />


                                                            {
                                                                ejercicioActual.tipo ===
                                                                    'fraccion'

                                                                    ?

                                                                    <EtiquetaFraccion
                                                                        x={
                                                                            toX(
                                                                                mark
                                                                            )
                                                                        }
                                                                        y={
                                                                            Y +
                                                                            62
                                                                        }
                                                                        numerador={
                                                                            ejercicioActual
                                                                                .numerador
                                                                        }
                                                                        denominador={
                                                                            ejercicioActual
                                                                                .denominador
                                                                        }
                                                                        color={
                                                                            OK
                                                                        }
                                                                    />

                                                                    :

                                                                    <text
                                                                        x={
                                                                            toX(
                                                                                mark
                                                                            )
                                                                        }
                                                                        y={
                                                                            Y +
                                                                            92
                                                                        }
                                                                        textAnchor="middle"
                                                                        fontSize="22"
                                                                        fontWeight="800"
                                                                        fill={
                                                                            OK
                                                                        }
                                                                    >
                                                                        {
                                                                            ejercicioActual
                                                                                .numerador
                                                                        }
                                                                    </text>
                                                            }

                                                        </g>
                                                    )
                                                }

                                            </g>
                                        )
                                    }


                                </RectaM150>


                                {/* ================================================= */}
                                {/* PONER / SACAR BANDEJA                             */}
                                {/* SOLO EN FRACCIONES                                 */}
                                {/* ================================================= */}

                                {
                                    !resuelto &&
                                    ejercicioActual.tipo ===
                                        'fraccion' && (

                                        <div
                                            style={{
                                                display:
                                                    'flex',

                                                justifyContent:
                                                    'center',

                                                alignItems:
                                                    'center',

                                                gap:
                                                    12,

                                                minHeight:
                                                    46,

                                                flexShrink:
                                                    0
                                            }}
                                        >

                                            {
                                                !bandejaVisible

                                                    ?

                                                    <button
                                                        style={{
                                                            ...btn,

                                                            opacity:

                                                                disable

                                                                    ?
                                                                    0.5

                                                                    :
                                                                    1
                                                        }}
                                                        disabled={
                                                            disable
                                                        }
                                                        onClick={
                                                            ponerBandeja
                                                        }
                                                    >
                                                        Poner bandeja
                                                    </button>

                                                    :

                                                    <button
                                                        style={{
                                                            ...btn,

                                                            opacity:

                                                                disable

                                                                    ?
                                                                    0.5

                                                                    :
                                                                    1
                                                        }}
                                                        disabled={
                                                            disable
                                                        }
                                                        onClick={
                                                            sacarBandeja
                                                        }
                                                    >
                                                        Sacar bandeja
                                                    </button>
                                            }

                                        </div>
                                    )
                                }


                                {/* ================================================= */}
                                {/* FEEDBACK                                           */}
                                {/* ================================================= */}

                                <p
                                    style={{
                                        minHeight:
                                            26,

                                        margin:
                                            0,

                                        padding:
                                            '0 10px',

                                        fontSize:
                                            18,

                                        fontWeight:
                                            600,

                                        textAlign:
                                            'center',

                                        color:

                                            feedback

                                                ?
                                                feedback.ok

                                                    ?
                                                    OK

                                                    :
                                                    MARK

                                                :
                                                'transparent'
                                    }}
                                    aria-live="polite"
                                >

                                    {
                                        feedback

                                            ?
                                            feedback.text

                                            :
                                            '.'
                                    }

                                </p>


                            </ContenidoM150>


                            {/* ===================================================== */}
                            {/* BOTTOM                                                */}
                            {/* ===================================================== */}

                            <BottomM150>


                                <BotoneraBottom
                                    miniInstrucciones={
                                        miniInstrucciones
                                    }
                                />


                                <AvanzarBtn
                                    disabled={
                                        disable ||
                                        resuelto
                                    }
                                    onClick={
                                        verificar
                                    }
                                >

                                    <AvanzarImg
                                        src="/img/play.png"
                                    />

                                </AvanzarBtn>


                            </BottomM150>


                        </PantallaM150>

                        :

                        null

                    :

                    <Instrucciones
                        miniInstrucciones={
                            miniInstrucciones
                        }
                    />
            }


        </Fragment>
    );
};


export default M150;